import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLuxonDateModule } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { NgChartsModule } from 'ng2-charts';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { IconComponent } from './icon/icon.component';
import { PopoverComponent } from './popover/popover.component';
import { PopoverDirective } from './popover/popover.directive';
import { StickyHeaderDirective } from './directives/sticky-header.directive';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TabsComponent } from './tabs/tabs.component';
import { TabsItemComponent } from './tabs/tabs-item/tabs-item.component';
import { TabsRouteComponent } from './tabs-route/tabs-route.component';
import { HasPermissionDirective } from './directives/has-permission.directive';
import { PermissionsTableComponent } from './permissions-table/permissions-table.component';
import { HelperCardComponent } from '@app/admin/helper-card/helper-card.component';
import { StepperComponent } from './stepper/stepper.component';
import { BookStepPipe } from './pipes/book-step.pipe';
import { DragToUploadComponent } from './drag-to-upload/drag-to-upload.component';
import { FilesizePipe } from '@shared/filesize-pipe/filesize.pipe';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { SpinnerComponent } from './spinner/spinner.component';
import { ButtonDropdownComponent } from './button-dropdown/button-dropdown.component';
import { MediaPreviewComponent } from './media-preview/media-preview.component';
import { HeaderComponent } from './header/header.component';
import { CloudStorageComponent } from './cloud-storage/cloud-storage.component';

import { PillBoxComponent } from './pill-box/pill-box.component';
import { MediaPreviewCarouselComponent } from './media-preview-carousel/media-preview-carousel.component';
import { MediaPreviewSlideComponent } from './media-preview-carousel/media-preview-slide/media-preview-slide.component';
import { WeekPickerComponent } from './date/week-picker/week-picker.component';
import { YearAndMonthPickerComponent } from './date/year-and-month-picker/year-and-month-picker.component';
import { YearAndMonthPickerHeader } from './date/year-and-month-picker/year-and-month-picker-header.component';
import { DatetimePickerComponent } from './datetime-picker/datetime-picker.component';
import { ButtonComponent } from './button/button/button.component';
import { NoDataComponent } from './components/no-data/no-data.component';
import {
  DashboardFilterComponent,
  StatisticsFilterComponent,
  ChartContainerComponent,
  KpiBoxDoubleComponent,
  KpiBoxSingleComponent,
  AgeGenderDistributionGraphComponent,
  AgeGenderDistributionDoughnutChartComponent,
  AgeGenderDistributionBarChartComponent,
  TotalsWeatherPerDayChartComponent,
  EmailSendoutsListComponent,
  EmailSendoutsItemComponent
} from './components/statistics';
import { EmailSendoutModalComponent } from './components/modals';
import { MediaStatsComponent } from './components/statistics/media-stats/media-stats.component';
import { MediaStatsItemComponent } from './components/statistics/media-stats/media-stats-item/media-stats-item.component';
import { NumberFormatPipe } from './pipes/number-format.pipe';
import { MultiSelectAutocompleteComponent } from './multi-select-autocomplete/multi-select-autocomplete.component';
import { BoldTextPipe } from './pipes/bold-text.pipe';
import { DateTranslatePipe } from './pipes/date-translate.pipe';
import { TimeFormatDirective } from './time-format.directive';
import {
  FormInputComponent,
  FormRadioGroupComponent,
  FormDateRangePickerComponent,
  FormWeekPickerComponent,
  FormDatePickerComponent,
  FormSelectComponent,
  FormComboboxComponent,
  FormSelectCountryComponent,
  FormSelectCurrencyComponent,
  FormRichTextEditor,
  FormTextareaComponent,
  FormCodeEditorComponent,
  TimeframeSelectComponent,
} from './form';
import { TimeframeOptionPartsPipe } from './form/timeframe-select/timeframe-option-parts.pipe';
import { SelectDropdownWithStatusToggleComponent } from './form/select-dropdown-with-status-toggle/select-dropdown-with-status-toggle.component';
import { ChartCustomLabelsComponent } from './components/statistics/chart-custom-labels/chart-custom-labels.component';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DATE_FORMATS } from '@app/constants';
import { FiltersComponent } from './filters/filters.component';
import { CalendarItemComponent, BookingItemComponent, ForecastItemComponent } from './calendar/calendar-item';
import { CalendarNavComponent } from './calendar/calendar-nav/calendar-nav.component';
import { MoreLinkDirective } from './directives/more-link.directive';
import { MatRippleModule } from '@angular/material/core';
import { DropdownButtonComponent } from './button/dropdown-button/dropdown-button.component';
import { EllipsisTooltipDirective } from './directives/ellipsis-tooltip.directive';
import { MediaUploadItemComponent } from '@app/admin/media/media-create/media-upload-item/media-upload-item.component';
import { SidePanelComponent } from './components/side-panel/side-panel.component';
import { AdditionalInstructionsModalComponent } from './manual/additional-instructions-modal/additional-instructions-modal.component';
import { QuillModule } from 'ngx-quill';
import { HistoryTableComponent } from './history/history-table/history-table.component';
import { HistoryModalComponent } from './history/history-modal/history-modal.component';
import { PermissionsHistoryTableComponent } from './history/permissions-history-table/permissions-history-table.component';
import { HistoryPermissionChangePipe } from './pipes/history-permission-change.pipe';
import { ActivityIconPipe } from './pipes/activity-icon.pipe';
import { InfoNoteComponent } from './components/info-note/info-note';
import { ValidationErrorComponent } from './form/validation-errors.component';
import { FutureDatePipe } from './pipes/future-date.pipe';
import { DecimalNumberLimitDirective } from './decimal-number-limit.directive';
import { PricingModelFullNamePipe } from './pipes/pricing-model.pipe';
import { SecondsToTimePipe } from './pipes/seconds-to-time.pipe';

const STANDALONE_COMPONENTS = [
  ButtonComponent,
  DropdownButtonComponent,
  NoDataComponent,
  ChartCustomLabelsComponent,
  InfoNoteComponent,
  ValidationErrorComponent,
  FormCodeEditorComponent,
];

const MATERIAL = [
  MatButtonModule,
  MatButtonToggleModule,
  MatSidenavModule,
  MatIconModule,
  MatDividerModule,
  MatListModule,
  MatProgressBarModule,
  MatSnackBarModule,
  MatCardModule,
  MatInputModule,
  MatFormFieldModule,
  MatToolbarModule,
  MatTableModule,
  MatSortModule,
  MatSelectModule,
  MatChipsModule,
  MatExpansionModule,
  MatCheckboxModule,
  MatDialogModule,
  MatTabsModule,
  MatTreeModule,
  MatDatepickerModule,
  MatLuxonDateModule,
  MatAutocompleteModule,
  MatSlideToggleModule,
  MatTooltipModule,
  MatMenuModule,
  MatRadioModule,
  MatSliderModule,
  MatProgressSpinnerModule,
  MatPaginatorModule,
  MatRippleModule,
  ClipboardModule,
];

const LIBRARIES = [
  QuillModule.forRoot()
]

const IMPORTS = [
  CommonModule,
  RouterModule,
  FormsModule,
  ReactiveFormsModule,
  TranslateModule,
  NgChartsModule,
  NgxMatIntlTelInputComponent,
  DragDropModule,
  ...MATERIAL,
  ...STANDALONE_COMPONENTS
];

const DECLARATIONS = [
  PopoverDirective,
  HasPermissionDirective,
  StickyHeaderDirective,
  MoreLinkDirective,
  EllipsisTooltipDirective,
  DecimalNumberLimitDirective,
  IconComponent,
  PopoverComponent,
  TabsComponent,
  TabsItemComponent,
  TabsRouteComponent,
  PermissionsTableComponent,
  HelperCardComponent,
  StepperComponent,
  BookStepPipe,
  FutureDatePipe,
  DragToUploadComponent,
  FilesizePipe,
  SpinnerComponent,
  ButtonDropdownComponent,
  MediaPreviewComponent,
  HeaderComponent,
  CloudStorageComponent,
  MediaPreviewCarouselComponent,
  MediaPreviewSlideComponent,
  PillBoxComponent,
  DashboardFilterComponent,
  StatisticsFilterComponent,
  WeekPickerComponent,
  YearAndMonthPickerComponent,
  YearAndMonthPickerHeader,
  DatetimePickerComponent,
  ChartContainerComponent,
  KpiBoxSingleComponent,
  KpiBoxDoubleComponent,
  AgeGenderDistributionGraphComponent,
  NumberFormatPipe,
  BoldTextPipe,
  AgeGenderDistributionDoughnutChartComponent,
  AgeGenderDistributionBarChartComponent,
  TotalsWeatherPerDayChartComponent,
  EmailSendoutModalComponent,
  EmailSendoutsListComponent,
  EmailSendoutsItemComponent,
  MultiSelectAutocompleteComponent,
  FormRadioGroupComponent,
  MultiSelectAutocompleteComponent,
  FormInputComponent,
  FormSelectComponent,
  FormDateRangePickerComponent,
  FormWeekPickerComponent,
  FormDatePickerComponent,
  FormComboboxComponent,
  TimeframeSelectComponent,
  TimeframeOptionPartsPipe,
  MultiSelectAutocompleteComponent,
  TimeFormatDirective,
  MediaStatsComponent,
  MediaStatsItemComponent,
  SelectDropdownWithStatusToggleComponent,
  FiltersComponent,
  FormSelectCountryComponent,
  CalendarItemComponent,
  CalendarNavComponent,
  BookingItemComponent,
  ForecastItemComponent,
  MediaUploadItemComponent,
  FormRichTextEditor,
  FormTextareaComponent,
  FormSelectCurrencyComponent,
  SidePanelComponent,
  AdditionalInstructionsModalComponent,
  HistoryTableComponent,
  HistoryModalComponent,
  PermissionsHistoryTableComponent,
  HistoryPermissionChangePipe,
  ActivityIconPipe,
  PricingModelFullNamePipe,
  SecondsToTimePipe
];

const PROVIDERS = [DateTranslatePipe, { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS }];

@NgModule({
  imports: [...IMPORTS, ...LIBRARIES],
  declarations: [...DECLARATIONS],
  exports: [...IMPORTS, ...DECLARATIONS],
  providers: [...PROVIDERS],
})
export class SharedModule { }
